import type { ProductMultiPrice } from '@root/src/types';
import type { PriceObjT } from './index.type';

export type PriceLayout = 'desktop' | 'mobile';

export interface ShownPriceSource {
  priceObj: PriceObjT;
  multiPricePromo: ProductMultiPrice | null;
  ifWeighting: boolean;
  isMenu: boolean;
  available: boolean | null;
}

type Price = number | null | undefined;

export interface ShownPrice {
  price: Price;
  oldPrice: Price;
}

const isBuyAtOncePromo = (
  promo: ProductMultiPrice | null,
  amountInCart: number
): promo is ProductMultiPrice =>
  !!promo &&
  typeof promo.buy_at_once === 'number' &&
  promo.buy_at_once > 0 &&
  amountInCart < promo.buy_at_once;

const promoPrice = (
  { priceObj, ifWeighting }: ShownPriceSource,
  promo: ProductMultiPrice
): Price => {
  if (priceObj.priceBlockWeightPrice) {
    return promo.weightPriceData?.price1Kg;
  }
  if (ifWeighting) {
    return promo.weightPriceData?.price;
  }
  return promo.price_active;
};

const desktopPromoOldPrice = ({
  priceObj,
  ifWeighting
}: ShownPriceSource): Price => {
  if (priceObj.priceBlockWeightPrice) {
    return priceObj.currentPriceField;
  }
  if (ifWeighting) {
    return priceObj.oldPriceField2;
  }
  return priceObj.discount
    ? priceObj.oldPriceField
    : priceObj.currentPriceField;
};

const desktopPrice = (
  source: ShownPriceSource,
  amountInCart: number
): ShownPrice => {
  const { priceObj, multiPricePromo } = source;
  if (isBuyAtOncePromo(multiPricePromo, amountInCart)) {
    return {
      price: promoPrice(source, multiPricePromo),
      oldPrice: desktopPromoOldPrice(source)
    };
  }
  if (priceObj.priceBlockWeightPrice) {
    return {
      price: priceObj.currentPriceField,
      oldPrice: priceObj.oldPriceField2 ? priceObj.oldPriceField : null
    };
  }
  return {
    price: priceObj.currentPriceField2,
    oldPrice: priceObj.oldPriceField2
  };
};

const mobilePrice = (
  source: ShownPriceSource,
  amountInCart: number
): ShownPrice => {
  const { priceObj, multiPricePromo } = source;
  if (priceObj.specialPriceData || (source.isMenu && !source.available)) {
    return {
      price: priceObj.currentPriceField2,
      oldPrice: priceObj.oldPriceField2
    };
  }
  const isPricePerMeasure =
    (priceObj.weight_of_goods ?? 0) > 1 && priceObj.measure_id === 2;
  const oldPrice = isPricePerMeasure
    ? priceObj.oldPriceField
    : priceObj.oldPriceField2;
  if (isBuyAtOncePromo(multiPricePromo, amountInCart)) {
    return { price: promoPrice(source, multiPricePromo), oldPrice };
  }
  return {
    price: isPricePerMeasure
      ? priceObj.currentPriceField
      : priceObj.currentPriceField2,
    oldPrice
  };
};

export const shownProductPrice = (
  source: ShownPriceSource,
  amountInCart: number,
  layout: PriceLayout
): ShownPrice =>
  layout === 'desktop'
    ? desktopPrice(source, amountInCart)
    : mobilePrice(source, amountInCart);
